import PublicNav from "../layouts/PublicNav";
import { Link } from "react-router-dom";
import { Coins, Gauge, ShieldCheck, ArrowRight, ChartLine, Database, Users } from "@phosphor-icons/react";
import useSiteContent from "../lib/useSiteContent";
import SeoHead from "../components/SeoHead";

/**
 * Why-oodel pages: /for-patrons, /for-businesses, /methodology.
 *
 * Every visible string on these pages is CMS-overridable via Site Content
 * keys. Falls back to the bundled copy below when a key is unset. Admin
 * fields live in Admin.jsx → "Why oodel — For Patrons page", "Why oodel —
 * For Businesses page", "Why oodel — Methodology page".
 */
function useT() {
  const c = useSiteContent();
  // T(key, fallback) — empty/whitespace overrides treated as "unset".
  const T = (k, fallback) => {
    const v = c[k];
    return v !== undefined && v !== null && String(v).trim() !== "" ? v : fallback;
  };
  // Convenience: read a boolean toggle (default ON if unset/missing).
  T.on = (k) => (c[k] === undefined || c[k] === null || String(c[k]).toLowerCase() !== "false");
  return T;
}

function Frame({ children }) { return (<><PublicNav /><main className="themable-public min-h-screen">{children}</main></>); }
function Hero({ overline, title, sub }) {
  return (
    <section className="bg-black text-white border-b-4 border-mango">
      <div className="max-w-[1100px] mx-auto px-6 py-24 md:py-32">
        <div className="overline text-mango mb-5" style={{ fontSize: '0.7rem' }}>{overline}</div>
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-3xl">{title}</h1>
        {sub && <p className="mt-6 text-neutral-300 text-lg max-w-2xl">{sub}</p>}
      </div>
    </section>
  );
}
function Section({ overline, title, children }) {
  return (
    <section className="max-w-[1100px] mx-auto px-6 py-16">
      {overline && <div className="overline mb-2 text-mango">{overline}</div>}
      {title && <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-8 border-b-2 border-black pb-4">{title}</h2>}
      {children}
    </section>
  );
}

export function ForPatrons() {
  const T = useT();
  return (
    <Frame>
      <SeoHead
        title={T("seo_for_patrons_title", "Earn rewards while shaping Pakistan's insights | oodel.")}
        description={T("seo_for_patrons_description", "Join 48,000+ verified panelists across Pakistan. Take 5-minute surveys, earn points, redeem real-world rewards via Foodpanda, Daraz, Easypaisa.")}
        canonical={`${(T("seo_canonical_base", "") || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/for-patrons`}
      />
      <Hero
        overline={T("patrons_hero_overline", "FOR PATRONS · CITIZENS OF PAKISTAN")}
        title={T("patrons_hero_title", "Your opinion is data. We pay you for it.")}
        sub={T("patrons_hero_sub", "Join 48,000+ verified panelists across all four provinces. Take 5-minute surveys, earn points, redeem real-world rewards.")}
      />
      <Section
        overline={T("patrons_steps_overline", "HOW IT WORKS")}
        title={T("patrons_steps_title", "Three simple steps")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200">
          {[
            [T("patrons_step1_num", "01"), T("patrons_step1_title", "Sign up free"), T("patrons_step1_body", "Verify your email and complete your demographic profile in under 2 minutes.")],
            [T("patrons_step2_num", "02"), T("patrons_step2_title", "Take surveys"), T("patrons_step2_body", "We send you 2-3 surveys a month matched to your demographic. Each takes 3-5 minutes.")],
            [T("patrons_step3_num", "03"), T("patrons_step3_title", "Earn & redeem"), T("patrons_step3_body", "50 points per completion. Cash out for Foodpanda, Daraz, Easypaisa, JazzCash and more.")],
          ].map(([n, t, d]) => (
            <div key={n} className="bg-white p-8">
              <div className="font-mono text-3xl text-mango font-bold">{n}</div>
              <h3 className="text-xl font-bold mt-3 tracking-tight">{t}</h3>
              <p className="text-neutral-600 mt-2 text-sm">{d}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section
        overline={T("patrons_perks_overline", "WHAT'S IN IT FOR YOU")}
        title={T("patrons_perks_title", "Earn while shaping the conversation")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card icon={<Coins size={32} />} title={T("patrons_perk1_title", "Real cash equivalents")} body={T("patrons_perk1_body", "Vouchers redeemable across the major Pakistani digital marketplaces — no minimum point thresholds for entry-level rewards.")} />
          <Card icon={<ShieldCheck size={32} />} title={T("patrons_perk2_title", "Privacy first")} body={T("patrons_perk2_body", "Your individual answers are never shared. Public reports only show aggregated and weighted percentages.")} />
          <Card icon={<ChartLine size={32} />} title={T("patrons_perk3_title", "Real influence")} body={T("patrons_perk3_body", "Your views show up in trackers cited by national news, businesses and policy researchers across Pakistan.")} />
        </div>
      </Section>
      <section className="bg-mango text-black border-y-4 border-black">
        <div className="max-w-[1100px] mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-3xl md:text-4xl font-black tracking-tighter">{T("patrons_cta_title", "Ready to earn while informing Pakistan?")}</h3>
            <p className="mt-2">{T("patrons_cta_sub", "It takes 90 seconds.")}</p>
          </div>
          {T.on("patrons_cta_button_enabled") && (
            <Link to="/register" className="bg-black text-white px-6 py-4 font-bold flex items-center gap-2" data-testid="patrons-cta-button">{T("patrons_cta_button", "Join the panel")} <ArrowRight size={18} /></Link>
          )}
        </div>
      </section>
    </Frame>
  );
}

export function ForBusinesses() {
  const T = useT();
  return (
    <Frame>
      <SeoHead
        title={T("seo_for_businesses_title", "Pakistan's Market Intelligence Platform for Brands | oodel.")}
        description={T("seo_for_businesses_description", "Continuous representative consumer panel for Pakistani brands. Brand health, audience sizing, concept testing — fielded weekly.")}
        canonical={`${(T("seo_canonical_base", "") || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/for-businesses`}
      />
      <Hero
        overline={T("biz_hero_overline", "FOR BUSINESSES · BRANDS · POLICY")}
        title={T("biz_hero_title", "Stop guessing. Start measuring Pakistan.")}
        sub={T("biz_hero_sub", "oodel. is the country's first continuously-fielded representative panel. Track sentiment, brand health, and category dynamics every wave — without commissioning a one-off project.")}
      />
      <Section
        overline={T("biz_tiers_overline", "WHO BUYS OODEL")}
        title={T("biz_tiers_title", "Two ways to work with us")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-neutral-200">
          <div className="bg-white p-8">
            <div className="overline text-mango mb-2">{T("biz_tier1_overline", "SUBSCRIBER · USD 2,499 / YEAR")}</div>
            <h3 className="text-2xl font-black tracking-tight">{T("biz_tier1_title", "National Intelligence Hub")}</h3>
            <p className="text-neutral-600 mt-3">{T("biz_tier1_body", "Continuous access to every public tracker, snapshot, BrandIndex score, sector landing page, and editorial deep-dive (audio + text). Best for strategy, marketing, and policy teams.")}</p>
            <ul className="mt-6 text-sm space-y-1 text-neutral-700">
              <li>· {T("biz_tier1_feat1", "All public trackers + crosstabs")}</li>
              <li>· {T("biz_tier1_feat2", "BrandIndex 2.0 ratings")}</li>
              <li>· {T("biz_tier1_feat3", "AI-drafted weekly briefings")}</li>
              <li>· {T("biz_tier1_feat4", "Audio Deep Dives (commute friendly)")}</li>
            </ul>
          </div>
          <div className="bg-black text-white p-8">
            <div className="overline text-mango mb-2">{T("biz_tier2_overline", "ENTERPRISE · USD 25K+ / YEAR")}</div>
            <h3 className="text-2xl font-black tracking-tight">{T("biz_tier2_title", "Private Insights Vault")}</h3>
            <p className="text-neutral-300 mt-3">{T("biz_tier2_body", "Everything in Subscriber, plus your own custom questions fielded on the panel within 48 hours. A locked private vault scoped to your team. Dedicated PM.")}</p>
            <ul className="mt-6 text-sm space-y-1 text-neutral-300">
              <li>· {T("biz_tier2_feat1", "Private waves & tracker commissioning")}</li>
              <li>· {T("biz_tier2_feat2", "Crosstab cross-filter (Female × Punjab × SEC A)")}</li>
              <li>· {T("biz_tier2_feat3", "Direct line to a PM")}</li>
              <li>· {T("biz_tier2_feat4", "API access")}</li>
            </ul>
          </div>
        </div>
      </Section>
      <Section
        overline={T("biz_usecases_overline", "USE-CASES")}
        title={T("biz_usecases_title", "Built for the questions you actually have")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card icon={<Gauge size={32} />} title={T("biz_uc1_title", "Brand health")} body={T("biz_uc1_body", "Track unaided awareness, consideration, NPS, and perceived quality across waves — with crosstabs by region, gender, age and SEC.")} />
          <Card icon={<Users size={32} />} title={T("biz_uc2_title", "Audience sizing")} body={T("biz_uc2_body", "Estimate the size and SEC mix of any segment using oodel.'s asset-based classification model.")} />
          <Card icon={<Database size={32} />} title={T("biz_uc3_title", "Concept testing")} body={T("biz_uc3_body", "Field a private wave with 5 prototype concepts on Wednesday; receive aggregated results by Monday.")} />
        </div>
      </Section>
      <section className="bg-mango text-black border-y-4 border-black">
        <div className="max-w-[1100px] mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-3xl md:text-4xl font-black tracking-tighter">{T("biz_cta_title", "Ready to talk?")}</h3>
            <p className="mt-2">{T("biz_cta_sub", "Email sales — we'll set up a 30-minute walkthrough.")}</p>
          </div>
          {T.on("biz_cta_button_enabled") && (
            <a href={T("biz_cta_mailto", "mailto:sales@oodel.co?subject=Pricing%20Inquiry")} className="bg-black text-white px-6 py-4 font-bold flex items-center gap-2" data-testid="biz-cta-button">{T("biz_cta_button", "Talk to Sales")} <ArrowRight size={18} /></a>
          )}
        </div>
      </section>
    </Frame>
  );
}

export function Methodology() {
  const T = useT();
  return (
    <Frame>
      <SeoHead
        title={T("seo_methodology_title", "How oodel. Measures Pakistan — Methodology")}
        description={T("seo_methodology_description", "Stratified sampling, asset-based SEC classification, and post-stratification weighting tied to PBS quotas.")}
        canonical={`${(T("seo_canonical_base", "") || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/methodology`}
      />
      <Hero
        overline={T("methodology_hero_overline", "METHODOLOGY")}
        title={T("methodology_hero_title", "How we make 1,000 voices speak for 230 million.")}
        sub={T("methodology_hero_sub", "Every oodel. number is the product of stratified sampling, asset-based weighting, and post-stratification. Here's how the sausage is made — transparently.")}
      />
      <Section
        overline={T("methodology_s1_overline", "01 · PANEL DESIGN")}
        title={T("methodology_s1_title", "The panel")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="text-neutral-700 mb-4">{T("methodology_s1_body", "oodel. operates a continuously-renewing online panel of verified Pakistani residents. Every panelist completes mobile-verified registration and passes through profile audits before any data they generate is included in published numbers.")}</p>
            <ul className="text-sm text-neutral-700 space-y-1">
              <li>· {T("methodology_s1_bullet1", "Mobile + email double opt-in")}</li>
              <li>· {T("methodology_s1_bullet2", "Asset-based SEC classification (no self-reporting)")}</li>
              <li>· {T("methodology_s1_bullet3", "Continuous quality scoring on response consistency, attention checks, and completion times")}</li>
            </ul>
          </div>
          <div className="bg-neutral-100 p-6 border-l-4 border-mango">
            <div className="overline mb-1">{T("methodology_panel_label", "PANEL SIZE")}</div>
            <div className="font-mono text-5xl font-bold">{T("methodology_panel_number", "48,217")}</div>
            <div className="overline text-neutral-500 mt-3">{T("methodology_panel_caption", "verified panelists across 5 regions")}</div>
          </div>
        </div>
      </Section>
      <Section
        overline={T("methodology_s2_overline", "02 · QUOTAS & WEIGHTING")}
        title={T("methodology_s2_title", "Representative by design, weighted by results")}
      >
        <p className="text-neutral-700 mb-6 max-w-3xl" dangerouslySetInnerHTML={{ __html: T("methodology_s2_body", "Every published wave enforces hard quotas across <strong>Gender × Age × Region × SEC</strong>. After fieldwork closes, we apply post-stratification weights to align the achieved sample with the latest Pakistan Bureau of Statistics distributions.") }} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-neutral-200">
          <Quota
            label={T("methodology_quota1_label", "GENDER")}
            line1={T("methodology_quota1_line1", "Male / Female / Other")}
            line2={T("methodology_quota1_line2", "(50/50/—)")}
          />
          <Quota
            label={T("methodology_quota2_label", "AGE BANDS")}
            line1={T("methodology_quota2_line1", "18-24, 25-34, 35-44")}
            line2={T("methodology_quota2_line2", "45-54, 55+")}
          />
          <Quota
            label={T("methodology_quota3_label", "REGION")}
            line1={T("methodology_quota3_line1", "Punjab 53%, Sindh 23%")}
            line2={T("methodology_quota3_line2", "KPK 14%, Bal/ICT/GB/AJK 10%")}
          />
          <Quota
            label={T("methodology_quota4_label", "SEC (ASSET-BASED)")}
            line1={T("methodology_quota4_line1", "A · B · C · D · E")}
            line2={T("methodology_quota4_line2", "Auto-computed")}
          />
        </div>
      </Section>
      <Section
        overline={T("methodology_s3_overline", "03 · ETL & CHARTS")}
        title={T("methodology_s3_title", "From answers to charts in minutes")}
      >
        <p className="text-neutral-700 max-w-3xl">{T("methodology_s3_body", "When a wave closes, our ETL pipeline computes weighted percentages and demographic crosstabs for every question — then publishes them as pre-aggregated JSON for sub-100ms chart loads. Every tracker we publish includes its sample size N alongside the percentage so you can judge confidence.")}</p>
      </Section>
      <Section
        overline={T("methodology_s4_overline", "04 · MARGIN OF ERROR")}
        title={T("methodology_s4_title", "How confident is a typical reading?")}
      >
        <table className="w-full text-sm border-collapse">
          <thead><tr className="border-y-2 border-black"><th className="text-left py-3 overline">{T("methodology_moe_header_n", "SAMPLE SIZE")}</th><th className="text-left overline">{T("methodology_moe_header_moe", "MARGIN OF ERROR (95%)")}</th></tr></thead>
          <tbody className="font-mono">
            <tr className="border-b border-neutral-200"><td className="py-3">{T("methodology_moe_row1_n", "N = 1,000")}</td><td>{T("methodology_moe_row1_moe", "±3.1pp")}</td></tr>
            <tr className="border-b border-neutral-200"><td className="py-3">{T("methodology_moe_row2_n", "N = 500")}</td><td>{T("methodology_moe_row2_moe", "±4.4pp")}</td></tr>
            <tr className="border-b border-neutral-200"><td className="py-3">{T("methodology_moe_row3_n", "N = 200")}</td><td>{T("methodology_moe_row3_moe", "±6.9pp")}</td></tr>
            <tr><td className="py-3">{T("methodology_moe_row4_n", "N = 100")}</td><td>{T("methodology_moe_row4_moe", "±9.8pp")}</td></tr>
          </tbody>
        </table>
      </Section>
    </Frame>
  );
}

function Card({ icon, title, body }) {
  return (
    <div className="border-2 border-black p-6 bg-white hard-shadow">
      <div className="text-mango mb-3">{icon}</div>
      <h3 className="text-xl font-bold tracking-tight">{title}</h3>
      <p className="text-neutral-600 mt-2 text-sm">{body}</p>
    </div>
  );
}
function Quota({ label, line1, line2 }) {
  return (
    <div className="bg-white p-5">
      <div className="overline mb-2">{label}</div>
      <div className="font-mono text-sm font-bold">{line1}</div>
      <div className="font-mono text-xs text-neutral-500 mt-1">{line2}</div>
    </div>
  );
}
